<template>
  <v-container class="wrapper">
    <div class="text-center">
      <p class="text-h4 gray">
        Learning Paths
      </p>
      <p class="text-body-1 subtitle mt-2">
        Compare your confirmed skills with a target occupation and get an ordered course sequence.
      </p>
    </div>

    <v-card class="card-form mt-6">
      <v-card-title class="white--text headline background-gradient">
        <v-icon class="white-icon">mdi-map-marker-path</v-icon>
        <span class="headline ml-3">Skill gap → courses</span>
      </v-card-title>
      <v-card-text class="pt-6">
        <v-autocomplete
          v-model="selectedOccupation"
          :loading="occupationLoading"
          :items="occupationItems"
          :search-input.sync="occupationSearch"
          hide-no-data
          hide-details
          clearable
          return-object
          label="Target occupation (ESCO)"
          prepend-icon="mdi-magnify"
          item-text="label"
          item-value="uri"
          solo
          flat
          @change="onOccupationSelected"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.label }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.uri" class="uri-sub">
                {{ item.uri }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <div class="mt-4 d-flex align-center flex-wrap">
          <v-btn
            color="primary"
            class="mr-3 mb-2"
            :disabled="!selectedOccupation || !selectedOccupation.uri"
            :loading="pathLoading"
            @click="loadSkillGapPath"
          >
            Calculate path
          </v-btn>
          <v-checkbox
            v-model="wantExplain"
            class="mt-0 pt-0 mb-2"
            hide-details
            label="Short explanation (F2) for top courses"
          ></v-checkbox>
        </div>

        <v-alert v-if="pathError" type="error" dense text class="mt-3">
          {{ pathError }}
        </v-alert>

        <div v-if="pathResult" class="mt-6">
          <p class="text-h6 mb-2">
            Gap for: {{ pathResult.occupation.label }}
          </p>
          <div class="counts mb-4">
            Profile skills: {{ pathResult.gap.counts.profileSkills }}
            · Essential missing: {{ pathResult.gap.counts.essentialMissing }}/{{ pathResult.gap.counts.essentialTotal }}
            · Optional missing: {{ pathResult.gap.counts.optionalMissing }}/{{ pathResult.gap.counts.optionalTotal }}
          </div>

          <p class="text-subtitle-1 font-weight-medium">Missing essential skills</p>
          <v-chip-group column>
            <v-chip
              v-for="skill in pathResult.gap.essential"
              :key="'e-' + skill.uri"
              small
              color="error"
              outlined
              class="ma-1"
            >
              {{ skill.label }}
            </v-chip>
            <span v-if="!pathResult.gap.essential.length" class="text-body-2 grey--text">
              None — essential skills covered.
            </span>
          </v-chip-group>

          <p class="text-subtitle-1 font-weight-medium mt-4">Missing optional skills</p>
          <v-chip-group column>
            <v-chip
              v-for="skill in pathResult.gap.optional"
              :key="'o-' + skill.uri"
              small
              color="warning"
              outlined
              class="ma-1"
            >
              {{ skill.label }}
            </v-chip>
            <span v-if="!pathResult.gap.optional.length" class="text-body-2 grey--text">
              None — optional skills covered.
            </span>
          </v-chip-group>

          <p class="text-subtitle-1 font-weight-medium mt-6">
            Suggested course sequence
          </p>
          <p class="text-caption grey--text mb-2">
            Openable published courses, ordered by gap-skill coverage then profile similarity (F1).
          </p>
          <v-list two-line>
            <v-list-item
              v-for="(course, index) in pathResult.courses"
              :key="course.course_id"
              :to="'/app/people/course/' + course.course_id"
              class="course-item mb-2"
            >
              <v-list-item-avatar size="48" class="mr-3">
                <v-img
                  :src="
                    VUE_APP_API_ENDPOINT +
                    'images/courses/' +
                    (course.image || 'default.jpg')
                  "
                  contain
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ index + 1 }}. {{ course.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ course.partner || '—' }}
                  <span v-if="course.gapSkillHits">
                    · covers {{ course.gapSkillHits }} gap skill{{ course.gapSkillHits === 1 ? '' : 's' }}
                  </span>
                  <span v-if="course.semantic_similarity != null">
                    · sim {{ Number(course.semantic_similarity).toFixed(3) }}
                  </span>
                </v-list-item-subtitle>
                <v-list-item-subtitle
                  v-if="course.explanation"
                  class="explanation mt-1"
                >
                  {{ course.explanation }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon color="primary">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="!pathResult.courses.length">
              <v-list-item-content>
                <v-list-item-title class="grey--text">
                  No openable courses found for this path.
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>
    </v-card>

    <!-- Legacy challenges view (pre-v2) -->
    <div
      v-if="userChallenges.length != 0 && Object.keys(userChallenges).length === 0"
      class="mt-6"
    >
      <p class="text-center">
        Please, configure in your profile any job challenge first so the platform can calculate the learning paths.
      </p>
    </div>
    <v-card
      class="card-form mt-4"
      v-for="(jobChallengeSkills, jobChallengeName) in userChallenges"
      :key="jobChallengeName"
    >
      <v-card-title class="white--text headline background-gradient">
        <v-icon class="white-icon">mdi-briefcase-check</v-icon>
        <span class="headline ml-3"
          >Skills and competences for: {{ jobChallengeName }}</span
        >
      </v-card-title>

      <div
        v-for="(_, essentialOrOptional) in jobChallengeSkills"
        :key="essentialOrOptional"
      >
        <v-expansion-panels>
          <v-expansion-panel
            v-for="(item2, j) in jobChallengeSkills[essentialOrOptional]"
            :key="j"
          >
            <v-expansion-panel-header disable-icon-rotate>
              {{ item2.skill_name }} ({{ essentialOrOptional }})
              <template v-slot:actions>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-if="
                        userSkills.filter(
                          (o) => o.skill_name === item2.skill_name
                        ).length > 0
                      "
                      color="success"
                    >
                      mdi-check-circle
                    </v-icon>
                    <v-icon v-else color="error" v-bind="attrs" v-on="on">
                      mdi-close-circle</v-icon
                    >
                  </template>
                  <span>You don't have this skill</span>
                </v-tooltip>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              {{ item2.skill_description }}
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card>
  </v-container>
</template>

<script>
const { catalogSuggestions, escoAutocompleteFetchInit, browserLang } = require("../../../lib/catalog-autocomplete");

export default {
  name: "Learning paths",
  data: () => ({
    userSkills: [],
    userChallenges: [],
    selectedOccupation: null,
    occupationSearch: null,
    occupationItems: [],
    occupationLoading: false,
    pathLoading: false,
    pathError: null,
    pathResult: null,
    wantExplain: false,
    VUE_APP_API_ENDPOINT: process.env.VUE_APP_API_ENDPOINT,
  }),
  watch: {
    occupationSearch(name) {
      if (!name || name === (this.selectedOccupation && this.selectedOccupation.label)) {
        return;
      }
      this.searchOccupations(name);
    },
  },
  async mounted() {
    try {
      await this.getData();
    } catch (error) {
      if (error.message.includes("Authentication failed")) {
        this.$router.push("/auth-error");
      }
    }
  },
  methods: {
    async getData() {
      let response = await fetch(
        process.env.VUE_APP_API_ENDPOINT + "people/learning-paths/",
        {
          headers: {
            "Auth-Token": this.$cookie.get("jwt_token"),
            "Accept-Language": browserLang(),
          },
        }
      );
      response = await response.json();
      if (response.success) {
        this.userSkills = response.result.userSkills;
        this.userChallenges = response.result.userChallenges;
      } else {
        throw new Error(response.message);
      }
    },
    async searchOccupations(name) {
      this.occupationLoading = true;
      let response;
      try {
        response = await fetch(
          process.env.VUE_APP_API_ENDPOINT +
            "v2/common/search-challenges-by-name",
          escoAutocompleteFetchInit(this, { name })
        );
        response = await response.json();
        if (response.success) {
          this.occupationItems = catalogSuggestions(response.result);
        }
      } catch (error) {
        console.log(error.message);
      }
      this.occupationLoading = false;
    },
    onOccupationSelected() {
      this.pathResult = null;
      this.pathError = null;
    },
    async loadSkillGapPath() {
      if (!this.selectedOccupation || !this.selectedOccupation.uri) return;
      this.pathLoading = true;
      this.pathError = null;
      try {
        let response = await fetch(
          process.env.VUE_APP_API_ENDPOINT + "v2/people/learning-path",
          {
            method: "POST",
            body: JSON.stringify({
              occupationUri: this.selectedOccupation.uri,
              limit: 20,
              explain: this.wantExplain === true,
              explainLimit: 3,
              lang: browserLang(),
            }),
            headers: {
              "Content-Type": "application/json",
              "Auth-Token": this.$cookie.get("jwt_token"),
              "Accept-Language": browserLang(),
            },
          }
        );
        response = await response.json();
        if (!response.success) {
          throw new Error(response.message || "Learning path failed");
        }
        this.pathResult = response.result;
      } catch (error) {
        this.pathError = error.message;
        this.pathResult = null;
        if (String(error.message || "").includes("Authentication failed")) {
          this.$router.push("/auth-error");
        }
      }
      this.pathLoading = false;
    },
  },
};
</script>

<style>
.white-icon {
  color: white !important;
}
.wrapper {
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 40px;
}
.gray {
  color: #222c3a;
}
.subtitle {
  color: #555;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}
.counts {
  color: #333;
  font-size: 0.95rem;
}
.uri-sub {
  font-size: 11px;
  word-break: break-all;
}
.course-item {
  border: 1px solid #eee;
  border-radius: 4px;
}
.explanation {
  white-space: normal;
  color: #444 !important;
  font-style: italic;
}

.background-gradient {
  color: white;
  box-shadow: 0 0 20px #eee;
  background-image: linear-gradient(to right, #047ec3 0%, #52ae32 100%);
}
</style>
